// Hover popups that stay on screen. placePopup is the one piece of placement math every popup uses (the roll-pill
// tips here, the creature hover card, the hunt strip, player previews, the streams list and the corner window):
// measure the popup after it renders, put it on its preferred side of the anchor, flip to the other side when it
// does not fit, and shift it to stay a margin inside the viewport. mountTips draws one shared tip for anything
// carrying data-tip, on hover and on keyboard focus (a tap focuses it on a phone).

export interface Box { left: number; top: number; right: number; bottom: number }
export type Side = 'above' | 'below' | 'right' | 'left';
export interface PlaceOpts {
  /** Where to try first; the opposite side is the fallback. Default above. */
  prefer?: Side;
  /** Space between the anchor and the popup. Default 6. */
  gap?: number;
  /** Space kept to every viewport edge. Default 8. */
  margin?: number;
  /** Above or below: center on the anchor, or line up with its left edge. Default center. */
  align?: 'center' | 'start';
}

const OPPOSITE: Record<Side, Side> = { above: 'below', below: 'above', right: 'left', left: 'right' };

/** Keeps a span of length `size` starting near `at` inside [margin, view - margin]; pinned to the start if it is too big. */
export function clampSpan(at: number, size: number, view: number, margin: number): number {
  return Math.max(margin, Math.min(at, view - size - margin));
}

/**
 * Where a w×h popup goes beside anchor `a` in a vw×vh viewport: its top-left corner and the side it ended on.
 * Every result keeps the popup `margin` inside each edge whenever it fits at all.
 */
export function placePopup(a: Box, w: number, h: number, vw: number, vh: number, opts: PlaceOpts = {}): { x: number; y: number; side: Side } {
  const gap = opts.gap ?? 6, m = opts.margin ?? 8;
  const prefer = opts.prefer ?? 'above';
  const vertical = prefer === 'above' || prefer === 'below';
  const at = (s: Side) => (s === 'above' ? a.top - gap - h : s === 'below' ? a.bottom + gap : s === 'right' ? a.right + gap : a.left - gap - w);
  const fits = (s: Side) => (s === 'above' ? at(s) >= m : s === 'below' ? at(s) + h <= vh - m : s === 'right' ? at(s) + w <= vw - m : at(s) >= m);
  const room = (s: Side) => (s === 'above' ? a.top : s === 'below' ? vh - a.bottom : s === 'right' ? vw - a.right : a.left);
  const other = OPPOSITE[prefer];
  const side = fits(prefer) ? prefer : fits(other) ? other : room(prefer) >= room(other) ? prefer : other;
  if (vertical) {
    const x0 = opts.align === 'start' ? a.left : (a.left + a.right) / 2 - w / 2;
    return { x: Math.round(clampSpan(x0, w, vw, m)), y: Math.round(clampSpan(at(side), h, vh, m)), side };
  }
  return { x: Math.round(clampSpan(at(side), w, vw, m)), y: Math.round(clampSpan(a.top, h, vh, m)), side };
}

/** Places `el` (position: fixed, already rendered) beside `anchor` in its own window. */
export function placeBeside(el: HTMLElement, anchor: Element, opts: PlaceOpts = {}) {
  const win = el.ownerDocument.defaultView ?? window;
  const p = placePopup(anchor.getBoundingClientRect(), el.offsetWidth, el.offsetHeight, win.innerWidth, win.innerHeight, opts);
  el.style.left = `${p.x}px`; el.style.top = `${p.y}px`;
  el.dataset.side = p.side;
  return p;
}

// ---------------- the shared data-tip popup ----------------
let box: HTMLElement | null = null;
let on: HTMLElement | null = null;
/** Where the mouse last was, so a tip whose anchor a re-render replaced can follow the new copy. */
let px = -1, py = -1, mouse = false;
let watch: MutationObserver | null = null;

function show(el: HTMLElement) {
  const text = el.dataset.tip;
  if (!text) return;
  if (!box) {
    box = document.createElement('div');
    box.className = 'tipbox'; box.id = 'tipbox'; box.hidden = true;
    box.setAttribute('role', 'tooltip');
    document.body.appendChild(box);
  }
  on = el;
  box.textContent = text;
  box.hidden = false;
  el.setAttribute('aria-describedby', 'tipbox');
  placeBeside(box, el, { prefer: 'above' });
  watch ??= new MutationObserver(anchorGone);
  watch.observe(document.body, { childList: true, subtree: true });
}

/**
 * A re-render dropped the anchor. Under a still mouse no pointer event comes, so the tip stayed with old numbers, or
 * over whatever took the anchor's place (a boss fight's board, 2026-10-26). It follows the fresh copy under the mouse,
 * else goes.
 */
function anchorGone() {
  if (!on || on.isConnected) return;
  const el = mouse ? (document.elementFromPoint(px, py) as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null : null;
  if (el) show(el); else hideTip();
}

export function hideTip() {
  if (on) on.removeAttribute('aria-describedby');
  on = null;
  if (box) box.hidden = true;
  watch?.disconnect();
}

let mounted = false;
export function mountTips() {
  if (mounted) return;
  mounted = true;
  const find = (e: Event) => (e.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
  const at = (e: PointerEvent) => { px = e.clientX; py = e.clientY; mouse = e.pointerType !== 'touch'; };
  document.addEventListener('pointermove', at, { passive: true });
  document.addEventListener('pointerdown', at, { passive: true });
  document.addEventListener('pointerover', (e) => {
    at(e);
    if (e.pointerType === 'touch') return;
    const el = find(e);
    if (el && el !== on) show(el);
  });
  document.addEventListener('pointerout', (e) => {
    if (!on || e.pointerType === 'touch') return;
    const to = e.relatedTarget as Node | null;
    if (!(to && on.contains(to))) hideTip();
  });
  document.addEventListener('focusin', (e) => { const el = find(e); if (el) show(el); else if (on) hideTip(); });
  document.addEventListener('focusout', (e) => { if (on && e.target === on) hideTip(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && on) hideTip(); });
  // The anchor moves with a scrolled panel; a re-render can drop it.
  window.addEventListener('scroll', () => { if (on) { if (on.isConnected) placeBeside(box!, on, { prefer: 'above' }); else hideTip(); } }, true);
  window.addEventListener('resize', hideTip);
}
